import { useState, useEffect, useRef } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useLiff } from '../../contexts/LiffContext';
import { LiffLoginGate } from '../../components/LiffLoginGate';
import { registerNewUser, checkGroupStatus } from '../../api/contextClient';
import { listMySquares, type SquareInfo } from '../../api/liteSquaresClient';
import { MainPage } from './MainPage';
import { HoldPage } from './HoldPage';
import { JoinPage } from './JoinPage';
import { SettingPage } from './SettingPage';
import { SubscriptionPlanPage } from './SubscriptionPlanPage';
import { ActivityResultPage } from './ActivityResultPage';
import { ActivityDeliverPage } from './ActivityDeliverPage';
import { getGroupIdFromUrl } from '../../utils/getGroupIdFromUrl';
import toast from 'react-hot-toast';
import { StorefrontIcon } from '@/components/StorefrontIcon';

/**
 * Lite 應用程式入口
 * 處理 /lite 相關的所有路由
 * 進入時會先檢查群組的 tenant 狀態
 */
export function LiteApp() {
  const {
    liff,
    isReady,
    isLoggedIn,
    shouldUseLiff,
    error: liffError,
  } = useLiff();

  const getLoginRedirectUri = () =>
    window.location.origin + window.location.pathname + window.location.search;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isChecking, setIsChecking] = useState(true);
  const [checkStatus, setCheckStatus] = useState<
    'checking' | 'no-tenant' | 'bound-to-other' | 'ready' | 'error'
  >('checking');
  const [groupId, setGroupId] = useState<string | null>(null); // 保存 groupId 供表單使用
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** 檢查群組狀態時取得的 JWT，供 no-tenant 時列出「我的商家」用 */
  const [jwtTokenFromCheck, setJwtTokenFromCheck] = useState<string | null>(
    null
  );
  const [mySquares, setMySquares] = useState<SquareInfo[]>([]);
  const [loadingMySquares, setLoadingMySquares] = useState(false);
  const [selectedSquareIdForBind, setSelectedSquareIdForBind] =
    useState<string>('');
  const hasCheckedRef = useRef(false);
  const isCheckingRef = useRef(false); // 追蹤是否正在檢查中
  const pendingNavigationRef = useRef<string | null>(null); // 追蹤待導航的路徑

  // 使用 ref 來保存最新的值，避免閉包問題
  const liffRef = useRef(liff);
  const isReadyRef = useRef(isReady);
  const isLoggedInRef = useRef(isLoggedIn);

  // 同步 ref 和 state
  useEffect(() => {
    liffRef.current = liff;
    isReadyRef.current = isReady;
    isLoggedInRef.current = isLoggedIn;
  }, [liff, isReady, isLoggedIn]);

  // no-tenant 時載入「我的 Lite 商家」（Squares）供選擇綁定
  useEffect(() => {
    if (checkStatus !== 'no-tenant' || !jwtTokenFromCheck) return;
    let cancelled = false;
    setLoadingMySquares(true);
    listMySquares(jwtTokenFromCheck)
      .then(list => {
        if (!cancelled) setMySquares(list);
      })
      .catch(() => {
        if (!cancelled) setMySquares([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMySquares(false);
      });
    return () => {
      cancelled = true;
    };
  }, [checkStatus, jwtTokenFromCheck]);

  // 處理建立 tenant 成功後的導航
  useEffect(() => {
    if (checkStatus === 'ready' && pendingNavigationRef.current) {
      const targetPath = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      console.log('[LiteApp] 執行待處理的導航:', targetPath);
      navigate(targetPath, { replace: true });
    }
  }, [checkStatus, navigate]);

  // 使用 sessionStorage 來追蹤是否已檢查（跨組件重新掛載也能保持）
  const CHECK_KEY = 'lite_app_group_checked';

  // 處理 LIFF path 參數（來自 Flex Message）
  useEffect(() => {
    const pathParam = searchParams.get('path');
    if (pathParam) {
      console.log('[LiteApp] 檢測到 LIFF path 參數:', pathParam);
      // 保留所有 query parameters（包括 groupId, activityId 等）
      const currentParams = new URLSearchParams(searchParams);
      currentParams.delete('path'); // 移除 path 參數，避免重複
      const queryString = currentParams.toString();
      const targetPath = queryString
        ? `${pathParam}?${queryString}`
        : pathParam;
      console.log('[LiteApp] 導向到:', targetPath);
      navigate(targetPath, { replace: true });
      return;
    }
  }, [searchParams, navigate]);

  // 只在組件掛載時執行一次檢查
  useEffect(() => {
    // 檢查 URL 中是否有 groupId（來自 Flex Message 按鈕）
    const groupIdFromUrl = getGroupIdFromUrl();

    // 如果 URL 中有 groupId 參數，清除之前的檢查狀態，強制重新檢查
    if (groupIdFromUrl) {
      console.log(
        '[LiteApp] 檢測到 URL 參數中的 groupId，清除之前的檢查狀態並重新檢查'
      );
      sessionStorage.removeItem(CHECK_KEY);
      hasCheckedRef.current = false;
    } else {
      // 如果已經檢查過（從 sessionStorage 或 ref），不再重複檢查
      const hasCheckedInSession = sessionStorage.getItem(CHECK_KEY) === 'true';
      if (hasCheckedInSession || hasCheckedRef.current) {
        if (hasCheckedInSession) {
          setIsChecking(false);
          setCheckStatus('ready');
        }
        return;
      }
    }

    // 如果是註冊頁面或 join 頁面，跳過檢查（這些頁面有自己的邏輯）
    const currentPath = window.location.pathname;
    if (
      currentPath === '/lite/register' ||
      currentPath === '/register' ||
      currentPath === '/lite/join'
    ) {
      setIsChecking(false);
      setCheckStatus('ready');
      hasCheckedRef.current = true;
      sessionStorage.setItem(CHECK_KEY, 'true');
      return;
    }

    // 等待 LIFF 準備好的函數
    const waitForLiffAndCheck = async () => {
      // 如果 LIFF 尚未準備好，等待一段時間後再試（最多等待 5 秒）
      let attempts = 0;
      const maxAttempts = 50; // 50 * 100ms = 5 秒

      while (!isReadyRef.current && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      // 如果 LIFF 還是沒準備好，檢查是否為 Mock 模式
      if (!isReadyRef.current) {
        // 如果是 Mock 模式，等待 Mock LIFF 初始化（通常很快）
        const { shouldUseMockLiff } = await import('../../hooks/useMockLiff');
        if (shouldUseMockLiff()) {
          // Mock 模式下，再等待一下讓 Mock LIFF 初始化
          await new Promise(resolve => setTimeout(resolve, 200));
          // 重新檢查
          if (!isReadyRef.current) {
            console.warn('[LiteApp] Mock LIFF 初始化超時，但仍嘗試繼續');
          }
        } else {
          // 非 Mock 模式且 LIFF 未準備好
          // 檢查是否有 groupId，如果有則繼續等待 LIFF 準備好以執行 tenant 檢查
          const groupIdFromUrl = getGroupIdFromUrl();
          if (groupIdFromUrl) {
            // 有 groupId，需要等待 LIFF 準備好以執行完整的 tenant 檢查
            // 繼續等待（最多再等 3 秒）
            let additionalAttempts = 0;
            const maxAdditionalAttempts = 30; // 30 * 100ms = 3 秒
            while (
              !isReadyRef.current &&
              additionalAttempts < maxAdditionalAttempts
            ) {
              await new Promise(resolve => setTimeout(resolve, 100));
              additionalAttempts++;
            }
            // 如果還是沒準備好，記錄警告但繼續執行（會在使用時檢查）
            if (!isReadyRef.current) {
              console.warn(
                '[LiteApp] 真實 LIFF 初始化超時，但會繼續嘗試執行 tenant 檢查'
              );
            }
          } else {
            // 沒有 groupId，直接允許訪問（可能是從外部連結進入）
            setIsChecking(false);
            setCheckStatus('ready');
            hasCheckedRef.current = true;
            sessionStorage.setItem(CHECK_KEY, 'true');
            return;
          }
        }
      }

      // 標記為已檢查，避免重複執行（在開始檢查前就設置）
      hasCheckedRef.current = true;
      sessionStorage.setItem(CHECK_KEY, 'true');

      // 如果不在 LIFF 環境或未登入，檢查是否為 Mock 模式
      if (!liffRef.current || !isLoggedInRef.current) {
        const { shouldUseMockLiff } = await import('../../hooks/useMockLiff');
        if (!shouldUseMockLiff()) {
          // 非 Mock 模式且未登入
          // 檢查是否有 groupId，如果有則需要等待 LIFF 準備好以執行 tenant 檢查
          const groupIdFromUrl = getGroupIdFromUrl();
          if (groupIdFromUrl) {
            // 有 groupId，需要等待 LIFF 準備好以執行完整的 tenant 檢查
            // 繼續等待（最多再等 3 秒）
            let additionalAttempts = 0;
            const maxAdditionalAttempts = 30; // 30 * 100ms = 3 秒
            while (
              (!liffRef.current || !isLoggedInRef.current) &&
              additionalAttempts < maxAdditionalAttempts
            ) {
              await new Promise(resolve => setTimeout(resolve, 100));
              additionalAttempts++;
            }
            // 如果還是沒準備好，記錄警告但繼續執行（會在使用時檢查）
            if (!liffRef.current || !isLoggedInRef.current) {
              console.warn(
                '[LiteApp] 真實 LIFF 未準備好或未登入，但會繼續嘗試執行 tenant 檢查'
              );
            }
          } else {
            // 沒有 groupId，直接允許訪問（可能是從外部連結進入）
            setIsChecking(false);
            setCheckStatus('ready');
            return;
          }
        } else {
          // Mock 模式下，即使 liff 為 null 或未登入，也應該有 Mock LIFF
          console.warn(
            '[LiteApp] Mock 模式下 liff 為 null 或未登入，但應該有 Mock LIFF'
          );
        }
      }

      // 標記為正在檢查，避免重複執行
      isCheckingRef.current = true;

      try {
        // 檢查 liff 是否存在
        if (!liffRef.current) {
          console.error('[LiteApp] ❌ liff 為 null，無法取得 Context');
          setIsChecking(false);
          setCheckStatus('ready');
          isCheckingRef.current = false;
          return;
        }

        // 取得 LIFF Context 來獲取 userId（不從 context 取得 groupId）
        console.log(
          '[LiteApp] 準備取得 LIFF Context，liff 物件:',
          liffRef.current
        );
        const context = await liffRef.current.getContext();
        // 記錄 LIFF Context（只用於取得 userId）
        console.log('[LiteApp] LIFF Context 內容:', {
          type: context?.type,
          userId: context?.userId,
          viewType: context?.viewType,
          endpointUrl: context?.endpointUrl,
        });

        // 只從 URL 取得 groupId（來自 Webhook Flex Message）
        let groupId = getGroupIdFromUrl();

        if (groupId) {
          console.log('[LiteApp] ✅ 從 URL 參數取得 groupId:', groupId);

          // 驗證 LINE Group ID 格式（必須以 C 開頭，33 字元，無連字號）
          const trimmedGroupId = groupId.trim();

          // 嚴格驗證：必須以 C 開頭，長度 33 字元，無連字號（不是 UUID）
          const isValidFormat =
            trimmedGroupId.startsWith('C') &&
            trimmedGroupId.length === 33 &&
            !trimmedGroupId.includes('-');

          if (!isValidFormat) {
            console.error('❌ [LiteApp] LINE Group ID 格式錯誤:', {
              groupId: trimmedGroupId,
              groupIdLength: trimmedGroupId.length,
              firstChar: trimmedGroupId.charAt(0),
              hasHyphen: trimmedGroupId.includes('-'),
              contextType: context?.type,
              isUUID:
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  trimmedGroupId
                ),
            });
            // 如果看起來像 UUID，可能是誤用了資料庫 ID
            if (trimmedGroupId.includes('-')) {
              console.error(
                '❌ [LiteApp] 嚴重錯誤：收到的是 UUID 格式，可能是誤用了資料庫的 tenant.id 或 user.id！'
              );
              console.error(
                '❌ [LiteApp] 請檢查 LIFF URL 是否正確包含 ?groupId=... 參數（應為 C 開頭，33 字元）'
              );
            }
            // 不拋出錯誤，但記錄警告並阻止使用
            console.warn(
              '⚠️ [LiteApp] 收到的 groupId 不是有效的 LINE Group ID（應以 C 開頭，33 字元，無連字號）'
            );
            // 清空 groupId，避免使用錯誤的值
            groupId = null;
          } else {
            console.log('✅ [LiteApp] LINE Group ID 格式驗證通過:', {
              groupIdPrefix: `${trimmedGroupId.substring(0, 3)}...`,
              groupIdLength: trimmedGroupId.length,
              firstChar: trimmedGroupId.charAt(0),
            });
            // 使用 trimmed 版本
            groupId = trimmedGroupId;
          }
        } else {
          console.error(
            '[LiteApp] ❌ URL 中沒有 groupId，Lite 系統必須從 LIFF URL 後綴取得 groupId'
          );
          console.error(
            '[LiteApp] 請確認 LIFF URL 包含 ?groupId=... 參數（應為 C 開頭，33 字元）'
          );
        }

        console.log('[LiteApp] 解析後的 groupId:', {
          hasGroupId: !!groupId,
          groupIdPrefix: groupId ? `${groupId.substring(0, 3)}...` : null,
          groupIdLength: groupId?.length || 0,
          groupIdFirstChar: groupId?.charAt(0) || null,
        });

        if (!groupId) {
          // 從 OA 或連結進入（無 groupId）：放行並導向 Square 入口，供商家在 OA 建立／管理
          console.log(
            '[LiteApp] 無 groupId（可能從 OA 開啟），導向 Square 入口 /lite/new-square'
          );
          setIsChecking(false);
          setCheckStatus('ready');
          setGroupId(null);
          isCheckingRef.current = false;
          sessionStorage.setItem(CHECK_KEY, 'true');
          navigate('/lite/new-square', { replace: true });
          return;
        }

        // 保存 groupId 供後續使用
        setGroupId(groupId);

        // 取得 LINE ID Token（用於註冊/登入）
        const lineIdToken = await liffRef.current.getIDToken();
        if (!lineIdToken) {
          console.error('[LiteApp] ❌ 無法取得 LINE ID Token，無法進行認證');
          setIsChecking(false);
          setCheckStatus('error');
          toast.error('無法取得 LINE 認證資訊，請重新開啟');
          isCheckingRef.current = false;
          return;
        }

        // 先註冊/登入取得 JWT Token
        let jwtToken: string | null = null;
        try {
          // 如果有 groupId，傳遞給後端
          const registerResult = await registerNewUser({
            lineIdToken,
            ...(groupId ? { groupId } : {}),
          });
          jwtToken = registerResult.jwtToken;
          console.log('[LiteApp] ✅ 註冊/登入成功，取得 JWT Token');
        } catch (error) {
          console.error('[LiteApp] ❌ 註冊/登入失敗:', error);
          setIsChecking(false);
          setCheckStatus('error');
          isCheckingRef.current = false;
          return;
        }

        if (!jwtToken) {
          console.error('[LiteApp] ❌ 無法取得 JWT Token');
          setIsChecking(false);
          setCheckStatus('error');
          isCheckingRef.current = false;
          return;
        }

        // 使用 JWT Token 檢查群組狀態（優先使用新的 JWT Token）
        try {
          const statusResult = await checkGroupStatus(groupId, jwtToken);

          console.log('[LiteApp] 群組狀態檢查結果:', {
            status: statusResult.status,
          });

          if (statusResult.status === 'BOUND_TO_YOU') {
            // 群組已綁定且屬於當前用戶，允許進入
            console.log('[LiteApp] 群組已綁定且屬於當前用戶，允許進入');

            // 群組已綁定，允許進入（不需要 tenantId）

            setIsChecking(false);
            setCheckStatus('ready');
            isCheckingRef.current = false;
            return;
          } else if (statusResult.status === 'BOUND_TO_OTHER') {
            // 群組已綁定給其他用戶，顯示錯誤
            console.log('[LiteApp] 群組已綁定給其他用戶，顯示錯誤');
            setIsChecking(false);
            setCheckStatus('bound-to-other');
            isCheckingRef.current = false;
            return;
          } else if (statusResult.status === 'NOT_REGISTERED') {
            // 群組未綁定，儲存 JWT 供後續列出「我的商家」或建立新商家
            setJwtTokenFromCheck(jwtToken);
            console.log('[LiteApp] 群組沒有 tenant，將顯示選擇商家或建立表單');
            setIsChecking(false);
            setCheckStatus('no-tenant');
            isCheckingRef.current = false;
            return;
          } else {
            // 未知狀態，不應該發生，顯示錯誤
            console.error(
              '[LiteApp] ❌ API 返回未知狀態:',
              statusResult.status
            );
            setIsChecking(false);
            setCheckStatus('error');
            toast.error('系統狀態異常，請稍後再試');
            isCheckingRef.current = false;
            return;
          }
        } catch (error) {
          console.error('[LiteApp] ❌ 檢查群組狀態失敗:', error);
          setIsChecking(false);
          setCheckStatus('error');
          isCheckingRef.current = false;
          return;
        }
      } catch (error) {
        console.error('[LiteApp] 檢查群組 tenant 狀態失敗:', error);
        setCheckStatus('error');
        setIsChecking(false);
        toast.error('檢查群組狀態失敗，請稍後再試');
        // 發生錯誤時，清除 sessionStorage 和重置標記，允許重試
        sessionStorage.removeItem(CHECK_KEY);
        hasCheckedRef.current = false;
        isCheckingRef.current = false;
      }
    };

    waitForLiffAndCheck();
  }, []); // 空依賴數組，只在組件掛載時執行一次

  // 顯示載入狀態
  if (isChecking) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在檢查群組狀態...</p>
        </div>
      </div>
    );
  }

  // 如果群組已綁定給其他用戶，顯示錯誤訊息
  if (checkStatus === 'bound-to-other') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            此群組已由其他管理員綁定
          </h2>
          <p className="text-gray-600 mb-6">
            此群組已經綁定給其他管理員，無法使用微型助理功能。如需使用，請聯繫該群組的管理員。
          </p>
          <button
            onClick={() => window.close()}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
          >
            關閉
          </button>
        </div>
      </div>
    );
  }

  // 如果沒有 tenant：有現成商家則顯示選擇綁定，否則顯示建立表單
  if (checkStatus === 'no-tenant') {
    const handleBindWithSelectedSquare = async () => {
      const square = mySquares.find(s => s.id === selectedSquareIdForBind);
      if (!square || !groupId) {
        toast.error('請選擇一個商家');
        return;
      }
      setIsSubmitting(true);
      try {
        const { getLiteJwtToken, setLiteJwtToken } = await import(
          '../../api/liteAuthToken'
        );
        let jwtToken = getLiteJwtToken();
        if (!jwtToken && liff) {
          const lineIdToken = await liff.getIDToken();
          if (lineIdToken) {
            const { registerNewUser } = await import('../../api/contextClient');
            const result = await registerNewUser({ lineIdToken });
            if (result.jwtToken) {
              setLiteJwtToken(result.jwtToken);
              jwtToken = result.jwtToken;
            }
          }
        }
        if (!jwtToken) {
          toast.error('請先登入');
          setIsSubmitting(false);
          return;
        }
        const { bindGroupWithSquare } = await import('../../api/contextClient');
        await bindGroupWithSquare(groupId, square.id, jwtToken);
        setCheckStatus('ready');
        toast.success('已綁定此群組');
      } catch (error) {
        console.error('[LiteApp] 綁定現有商家失敗:', error);
        toast.error(
          error instanceof Error ? error.message : '綁定失敗，請稍後再試'
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="mb-4">
              <StorefrontIcon className="w-16 h-16" withBorder />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              選擇要綁定到此群組的商家
            </h2>
            <p className="text-sm text-gray-600">
              選擇一個已有商家綁定此 LINE 群組。商家請先從 OA 建立，再來此綁定。
            </p>
          </div>
          {loadingMySquares ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent mx-auto mb-3" />
              <p className="text-sm text-gray-600">載入您的商家列表...</p>
            </div>
          ) : mySquares.length > 0 ? (
            <div className="space-y-4">
              {mySquares.map(sq => (
                <label
                  key={sq.id}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedSquareIdForBind === sq.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="bindSquare"
                    value={sq.id}
                    checked={selectedSquareIdForBind === sq.id}
                    onChange={() => setSelectedSquareIdForBind(sq.id)}
                    className="mr-3 h-4 w-4 text-blue-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {sq.name}
                    </div>
                    {sq.type && (
                      <div className="text-sm text-gray-500">{sq.type}</div>
                    )}
                  </div>
                </label>
              ))}
              <button
                type="button"
                onClick={handleBindWithSelectedSquare}
                disabled={isSubmitting || !selectedSquareIdForBind}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg"
              >
                {isSubmitting ? '綁定中...' : '確認綁定'}
              </button>
            </div>
          ) : (
            <div className="py-6 px-4 text-center rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-gray-700 font-medium mb-1">您尚無商家</p>
              <p className="text-sm text-gray-600">
                請先從 LINE 官方帳號 (OA) 建立商家，再回到此群組綁定。
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 如果檢查失敗，顯示錯誤訊息
  if (checkStatus === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">檢查失敗</h2>
          <p className="text-gray-600 mb-6">無法檢查群組狀態，請稍後再試。</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  // 正常顯示路由
  return (
    <LiffLoginGate
      isReady={isReady}
      shouldUseLiff={shouldUseLiff}
      isLoggedIn={isLoggedIn}
      error={liffError}
      onLoginClick={() => liff?.login?.({ redirectUri: getLoginRedirectUri() })}
    >
      <>
        <Routes>
          {/* 主頁面 */}
          <Route path="/" element={<MainPage />} />

          {/* 建立清單頁面 */}
          <Route path="/hold" element={<HoldPage />} />

          {/* 參與清單頁面 */}
          <Route path="/join" element={<JoinPage />} />

          {/* 助理設定頁面 */}
          <Route path="/setting" element={<SettingPage />} />

          {/* 訂閱方案頁面 */}
          <Route path="/subscription" element={<SubscriptionPlanPage />} />

          {/* 活動交付列表已搬到主頁，這裡直接導回主頁（減少 route 階層） */}
          <Route path="/deliver" element={<Navigate to="." replace />} />

          {/* 活動交付查看頁面（查看參與狀況，不能編輯） */}
          <Route
            path="/deliver/:activityId"
            element={<ActivityDeliverPage />}
          />

          {/* 活動結果頁面 */}
          <Route
            path="/deliver/:activityId/result"
            element={<ActivityResultPage />}
          />
        </Routes>
      </>
    </LiffLoginGate>
  );
}
